import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, LogOut, ArrowRight, ShieldAlert } from "lucide-react";
import { restrictedMe, setRestrictedToken, getRestrictedToken } from "@/lib/api";
import { loadRestrictedCatalog } from "@/lib/restrictedCatalog";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const CRIMSON = "#C8102E";

export default function RestrictedShop() {
    const navigate = useNavigate();
    const { addToCart, formatPrice } = useApp();
    const [me, setMe] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!getRestrictedToken()) {
            navigate("/restricted/login");
            return;
        }
        let cancelled = false;
        (async () => {
            // Load catalog first (resilient: cache → network → fallback)
            try {
                const { products: ps, source } = await loadRestrictedCatalog();
                if (cancelled) return;
                setProducts(ps);
                if (source === "fallback") {
                    toast.message("Showing cached catalog · refresh shortly", { duration: 2500 });
                }
            } catch (err) {
                if (cancelled) return;
                if (err?.response?.status === 401) {
                    setRestrictedToken(null);
                    toast.error("Session expired, please sign in again");
                    navigate("/restricted/login");
                    return;
                }
            }
            // Fetch user separately so a /auth/me failure doesn't blank the catalog
            try {
                const meRes = await restrictedMe();
                if (!cancelled) setMe(meRes.user);
            } catch {
                /* fail silently — products still render */
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [navigate]);

    const logout = () => {
        setRestrictedToken(null);
        toast.success("Signed out");
        navigate("/restricted/login");
    };

    const addCart = (p) => {
        addToCart({
            id: p.id,
            slug: p.slug,
            name: p.name,
            dosage: p.dosage,
            price_usd: p.price_usd,
            image: p.front_image || p.image,
            molecule: p.molecule,
            region: p.region,
        }, 1);
        toast.success(`${p.name} added to cart`);
    };

    return (
        <div className="min-h-screen bg-ink-base text-ink" data-testid="restricted-shop-page">
            {/* Crimson banner */}
            <div className="border-b" style={{ borderColor: "#3a1418", background: "linear-gradient(180deg, rgba(200,16,46,0.06), transparent)" }}>
                <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-10 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Lock className="w-3.5 h-3.5" style={{ color: CRIMSON }} strokeWidth={2} />
                            <p className="overline text-[11px] tracking-[0.22em]" style={{ color: CRIMSON }}>/ RESTRICTED CATALOG · MEMBERS ONLY</p>
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[0.92]">
                            Advanced<br /><span style={{ color: CRIMSON }}>research compounds.</span>
                        </h1>
                    </div>
                    {me && (
                        <div className="text-right space-y-2">
                            <p className="text-xs text-ink-dim">Signed in as <span className="text-ink font-mono">{me.username}</span></p>
                            <button onClick={logout} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-ink-dim hover:text-ink transition-colors" data-testid="restricted-logout-btn">
                                <LogOut className="w-3.5 h-3.5" /> SIGN OUT
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-12">
                <div className="mb-10 p-5 border flex items-start gap-3" style={{ borderColor: "#3a1418", background: "rgba(200,16,46,0.04)" }}>
                    <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" style={{ color: CRIMSON }} strokeWidth={1.5} />
                    <p className="text-[11px] font-mono uppercase tracking-[0.15em] leading-relaxed" style={{ color: CRIMSON }}>
                        STRICTLY FOR LABORATORY AND IN-VITRO RESEARCH PURPOSES ONLY. NOT FOR HUMAN OR ANIMAL CONSUMPTION.
                    </p>
                </div>

                {loading && <p className="text-ink-mute text-sm font-mono">LOADING…</p>}

                {!loading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="restricted-product-grid">
                        {products.map((p) => (
                            <div key={p.id}
                                className="group border bg-ink-surface hover:bg-ink-base/40 transition-colors flex flex-col"
                                style={{ borderColor: "#3a1418" }}
                                data-testid={`restricted-product-${p.id}`}>
                                <Link to={`/restricted/product/${p.slug}`} className="block">
                                    <div className="aspect-square overflow-hidden border-b" style={{ borderColor: "#3a1418" }}>
                                        <img src={p.front_image || p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                                    </div>
                                </Link>
                                <div className="p-5 flex flex-col flex-1">
                                    <p className="overline text-[10px] tracking-[0.18em]" style={{ color: CRIMSON }}>{p.category}</p>
                                    <Link to={`/restricted/product/${p.slug}`}>
                                        <h3 className="mt-2 font-display text-xl font-bold text-ink group-hover:text-[#C8102E] transition-colors">{p.name}</h3>
                                    </Link>
                                    <p className="mt-1 text-xs font-mono text-ink-dim">{p.dosage}</p>
                                    <p className="mt-3 text-xs text-ink-dim leading-relaxed line-clamp-3 flex-1">{p.short}</p>
                                    <div className="mt-5 flex items-center justify-between gap-3 pt-4 border-t" style={{ borderColor: "#2a1418" }}>
                                        <span className="font-mono text-lg font-bold text-ink">{formatPrice(p.price_usd)}</span>
                                        <button
                                            onClick={(e) => { e.preventDefault(); addCart(p); }}
                                            disabled={p.in_stock === false}
                                            className="px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] disabled:opacity-40"
                                            style={{ background: CRIMSON, color: "#fff" }}
                                            data-testid={`restricted-add-cart-${p.id}`}
                                        >
                                            {p.in_stock === false ? "OUT OF STOCK" : "ADD TO CART"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-16 pt-8 border-t" style={{ borderColor: "#2a1418" }}>
                    <p className="text-[10px] font-mono uppercase tracking-[0.18em] leading-relaxed" style={{ color: `${CRIMSON}cc` }}>
                        Strictly for laboratory and in-vitro research purposes only. Not for human or animal consumption.
                    </p>
                </div>
            </div>
        </div>
    );
}
